import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface SystemStatus {
  status: 'up' | 'down';
  message: string;
  estimated_completion: string | null;
}

interface SystemStatusContextType {
  systemStatus: SystemStatus;
  isMaintenanceMode: boolean;
  refreshStatus: () => Promise<void>;
}

const SystemStatusContext = createContext<SystemStatusContextType | undefined>(undefined);

export const SystemStatusProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [systemStatus, setSystemStatus] = useState<SystemStatus>({
    status: 'up',
    message: '',
    estimated_completion: null,
  });

  const fetchStatus = async () => {
    try {
      const response = await fetch('/status.json', {
        cache: 'no-store', // Always fetch fresh status
        headers: {
          'Cache-Control': 'no-cache',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setSystemStatus(data);
      } else {
        console.warn('[SystemStatus] Failed to fetch status, assuming system is up');
      }
    } catch (error) {
      console.warn('[SystemStatus] Error fetching status, assuming system is up:', error);
      // If we can't fetch status.json, assume system is up (fail open)
    }
  };

  useEffect(() => {
    fetchStatus();
    
    // Check status every 60 seconds
    const interval = setInterval(fetchStatus, 60000);
    
    return () => clearInterval(interval);
  }, []);

  const value: SystemStatusContextType = {
    systemStatus,
    isMaintenanceMode: systemStatus.status === 'down',
    refreshStatus: fetchStatus,
  };

  return (
    <SystemStatusContext.Provider value={value}>
      {children}
    </SystemStatusContext.Provider>
  );
};

export const useSystemStatus = () => {
  const context = useContext(SystemStatusContext);
  if (context === undefined) {
    throw new Error('useSystemStatus must be used within a SystemStatusProvider');
  }
  return context;
};
